import { useEffect, useRef, useState } from 'react'
import { EVENT_TYPE_LABELS } from '../utils/helpers'

function EventsStream({ webSocketService }) {
  const [events, setEvents] = useState([])
  const [isConnected, setIsConnected] = useState(false)
  const [maxEvents] = useState(50)
  const eventsContainerRef = useRef(null)

  // Helper function for escaping HTML
  const escapeHtml = (text) => {
    if (typeof text !== 'string') return String(text)
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Get event styling based on type
  const getEventStyle = (type) => {
    switch (type) {
      case 'system':
        return { backgroundColor: '#e3f2fd', color: '#1976d2' }
      case 'offer_created':
      case 'availability_created':
        return { backgroundColor: '#e8f5e8', color: '#2e7d32' }
      case 'activation_acknowledged':
        return { backgroundColor: '#fff3e0', color: '#f57c00' }
      case 'cbp_event':
        return { backgroundColor: '#f3e5f5', color: '#7b1fa2' }
      case 'telemetry_received':
        return { backgroundColor: '#e0f2f1', color: '#00695c' }
      case 'error':
        return { backgroundColor: '#ffeaea', color: '#d32f2f' }
      case 'notification':
        return { backgroundColor: '#fff3e0', color: '#f57c00' }
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' }
    }
  }

  // Format event data for display
  const formatEventData = (data) => {
    if (!data || Object.keys(data).length === 0) {
      return ''
    }

    // Handle different types of event data
    if (data.uvamId && data.capacityMW) {
      return `UVAM: ${data.uvamId}, Capacità: ${data.capacityMW} MW`
    }

    if (data.offerId && data.marketId) {
      return `Mercato: ${data.marketId}, ID: ${data.offerId}`
    }

    if (data.orderId && data.status) {
      return `Ordine: ${data.orderId}, Status: ${data.status}`
    }

    if (data.connectionId) {
      return `ID: ${data.connectionId}`
    }

    if (data.message) {
      return escapeHtml(data.message)
    }

    // Generic object display
    const keys = Object.keys(data).slice(0, 3) // Show max 3 properties
    return keys.map(key => {
      const value = data[key]
      if (typeof value === 'object') {
        return `${key}: [object]`
      }
      return `${key}: ${String(value).slice(0, 20)}${String(value).length > 20 ? '...' : ''}`
    }).join(', ')
  }

  // Add new event
  const addEvent = (type, title, data) => {
    const event = {
      id: Date.now() + Math.random(),
      timestamp: new Date(),
      type,
      title,
      data
    }

    setEvents(prevEvents => {
      const newEvents = [event, ...prevEvents]
      // Keep only the last maxEvents
      return newEvents.slice(0, maxEvents)
    })

    // Auto-scroll to top for new events
    setTimeout(() => {
      if (eventsContainerRef.current) {
        eventsContainerRef.current.scrollTop = 0
      }
    }, 100)
  }

  // Clear all events
  const clearEvents = () => {
    setEvents([])
  }

  // Setup WebSocket listeners
  useEffect(() => {
    if (!webSocketService) {
      addEvent('system', 'WebSocket service non disponibile', {})
      return
    }

    // Add initial status
    addEvent('system', 'Configurazione WebSocket', { 
      connected: webSocketService.isConnected,
      url: webSocketService.url 
    })

    // Try to connect and handle connection failure gracefully
    if (!webSocketService.isConnected) {
      webSocketService.connect().catch(error => {
        addEvent('error', 'Connessione WebSocket fallita', { 
          error: error.message,
          note: 'Funzionalità limitate disponibili'
        })
        // Simulate connection for demo purposes
        setTimeout(() => {
          setIsConnected(false)
          addEvent('system', 'Modalità offline - WebSocket non disponibile', {})
        }, 1000)
      })
    }

    const onConnected = () => {
      setIsConnected(true)
      addEvent('system', 'WebSocket connesso', {})
    }

    const onDisconnected = (event) => {
      setIsConnected(false)
      addEvent('system', 'WebSocket disconnesso', {
        code: event.code,
        reason: event.reason || 'Connessione persa'
      })
    }

    const onWelcome = (message) => {
      addEvent('welcome', 'Benvenuto', {
        connectionId: message.connectionId,
        user: message.user
      })
    }

    const onError = (error) => {
      addEvent('error', 'Errore WebSocket', error)
    }

    const onOfferCreated = (data) => {
      addEvent('offer_created', 'Offerta Creata', data)
    }

    const onAvailabilityCreated = (data) => {
      addEvent('availability_created', 'Disponibilità Pubblicata', data)
    }

    const onActivationAcknowledged = (data) => {
      addEvent('activation_acknowledged', 'Attivazione Confermata', data)
    }

    const onCbpEvent = (data) => {
      addEvent('cbp_event', 'Evento CBP', data)
    }

    const onTelemetryReceived = (data) => {
      addEvent('telemetry_received', 'Telemetria Ricevuta', data)
    }

    const onEvent = (message) => {
      addEvent(message.eventType, EVENT_TYPE_LABELS[message.eventType] || message.eventType, message.data)
    }

    const onNotification = (notification) => {
      addEvent('notification', notification.title || 'Notifica', {
        message: notification.message,
        level: notification.level
      })
    }

    // Register event listeners
    webSocketService.on('connected', onConnected)
    webSocketService.on('disconnected', onDisconnected)
    webSocketService.on('welcome', onWelcome)
    webSocketService.on('error', onError)
    webSocketService.on('offer_created', onOfferCreated)
    webSocketService.on('availability_created', onAvailabilityCreated)
    webSocketService.on('activation_acknowledged', onActivationAcknowledged)
    webSocketService.on('cbp_event', onCbpEvent)
    webSocketService.on('telemetry_received', onTelemetryReceived)
    webSocketService.on('event', onEvent)
    webSocketService.on('notification', onNotification)

    // Cleanup listeners on unmount
    return () => {
      webSocketService.off('connected', onConnected)
      webSocketService.off('disconnected', onDisconnected)
      webSocketService.off('welcome', onWelcome)
      webSocketService.off('error', onError)
      webSocketService.off('offer_created', onOfferCreated)
      webSocketService.off('availability_created', onAvailabilityCreated)
      webSocketService.off('activation_acknowledged', onActivationAcknowledged)
      webSocketService.off('cbp_event', onCbpEvent)
      webSocketService.off('telemetry_received', onTelemetryReceived)
      webSocketService.off('event', onEvent)
      webSocketService.off('notification', onNotification)
    }
  }, [webSocketService])

  const renderEventItem = (event) => {
    const timeStr = event.timestamp.toLocaleTimeString('it-IT')
    const eventStyle = getEventStyle(event.type)
    const dataStr = formatEventData(event.data)

    return (
      <div 
        key={event.id}
        className="MuiListItem-root event-item" 
        style={{ 
          padding: '12px 16px', 
          borderBottom: '1px solid #e0e0e0', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px' 
        }}
      >
        <div 
          className="MuiChip-root event-time" 
          style={{ 
            backgroundColor: '#e0e0e0', 
            color: '#666', 
            padding: '2px 8px', 
            borderRadius: '12px', 
            fontSize: '0.7rem', 
            fontFamily: 'monospace', 
            minWidth: '60px', 
            textAlign: 'center' 
          }}
        >
          {timeStr}
        </div>
        <div 
          className="MuiChip-root event-type" 
          style={{ 
            ...eventStyle,
            padding: '4px 8px', 
            borderRadius: '12px', 
            fontSize: '0.75rem', 
            fontWeight: '500' 
          }}
        >
          {event.title}
        </div>
        {dataStr && (
          <div 
            className="event-data MuiTypography-body2" 
            style={{ 
              color: '#666', 
              fontSize: '0.8rem', 
              flex: 1 
            }}
          >
            {dataStr}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="MuiPaper-root MuiCard-root" style={{ padding: '24px', margin: '16px 0' }}>
      
      {/* Header with Connection Status and Controls */}
      <div 
        className="MuiCardHeader-root" 
        style={{ 
          paddingBottom: '16px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}
      >
        <div className="MuiCardHeader-content">
          <h2 className="MuiTypography-h5" style={{ margin: 0, color: '#1976d2' }}>
            Eventi Real-time
          </h2>
          <p className="MuiTypography-body2" style={{ color: '#666', margin: '8px 0 0 0' }}>
            Stream degli eventi WebSocket dal server
          </p>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div 
            className="connection-status MuiChip-root" 
            style={{ 
              padding: '4px 12px', 
              borderRadius: '16px', 
              fontSize: '0.75rem',
              backgroundColor: isConnected ? '#e8f5e8' : '#ffeaea',
              color: isConnected ? '#2e7d32' : '#d32f2f'
            }}
          >
            <span 
              className="status-dot" 
              style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                display: 'inline-block', 
                marginRight: '8px', 
                backgroundColor: isConnected ? '#4caf50' : '#f44336'
              }}
            />
            <span className="status-text">{isConnected ? 'Connesso' : 'Disconnesso'}</span>
          </div>
          
          <button 
            onClick={clearEvents}
            className="MuiButton-root MuiButton-outlined MuiButton-outlinedSecondary"
            style={{ 
              border: '1px solid #757575', 
              color: '#757575', 
              padding: '6px 12px', 
              borderRadius: '4px', 
              background: 'transparent', 
              cursor: 'pointer', 
              fontSize: '0.75rem' 
            }}
          >
            Pulisci
          </button>
        </div>
      </div>

      {/* Events List Container */}
      <div 
        className="MuiList-root events-container" 
        ref={eventsContainerRef}
        style={{ 
          maxHeight: '400px', 
          overflowY: 'auto', 
          border: '1px solid #e0e0e0', 
          borderRadius: '4px', 
          backgroundColor: '#fafafa' 
        }}
      >
        {events.length === 0 ? (
          <div 
            className="MuiListItem-root event-item" 
            style={{ 
              padding: '12px 16px', 
              borderBottom: '1px solid #e0e0e0', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px' 
            }}
          >
            <div 
              className="MuiChip-root event-time" 
              style={{ 
                backgroundColor: '#e0e0e0', 
                color: '#666', 
                padding: '2px 8px', 
                borderRadius: '12px', 
                fontSize: '0.7rem', 
                fontFamily: 'monospace', 
                minWidth: '60px', 
                textAlign: 'center' 
              }}
            >
              --:--:--
            </div>
            <div 
              className="MuiChip-root event-type" 
              style={{ 
                backgroundColor: '#fff3e0', 
                color: '#f57c00', 
                padding: '4px 8px', 
                borderRadius: '12px', 
                fontSize: '0.75rem', 
                fontWeight: '500' 
              }}
            >
              Nessun evento
            </div>
            <div 
              className="event-data MuiTypography-body2" 
              style={{ 
                color: '#666', 
                fontSize: '0.8rem', 
                flex: 1 
              }}
            >
              In attesa di eventi...
            </div>
          </div>
        ) : (
          events.map(event => renderEventItem(event))
        )}
      </div>
    </div>
  )
}

export default EventsStream
